<!--
  A `PENDING` recognition is a plan, `RECOGNIZED` a posted fact with a real entry behind it,
  `CANCELLED` a plan that will never happen - three different truths that must not read
  alike (the brief's own words, and the same defect class Rule 2/AGENTS.md's colour
  vocabulary already forbids for a projected figure). Only `RECOGNIZED` gets `StatusTag`'s
  icon-plus-tint treatment and a link to its entry; `PENDING` is plain muted text with no
  icon; `CANCELLED` adds a strikethrough on top of that same muted text, so status is never
  carried by colour alone and every status is reachable from text content too.
-->
<script setup lang="ts">
import { formatDate, formatLocalDate, type LocalDate, type Money, parseLocalDate } from '@taler/domain'
import { ActionButton, ErrorState, Select, StatusTag } from '@taler/ui'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ExpenseAccountOption } from '../internal/expense-accounts.ts'
import type { PrepaidRecognitionStatus, PrepaidSchedule } from '../internal/types.ts'

const props = defineProps<{
  originEntryDate: LocalDate
  schedule: PrepaidSchedule | undefined
  notScheduled: boolean
  isLoading: boolean
  error: Error | null
  retrying: boolean
  expenseAccountOptions: readonly ExpenseAccountOption[]
  isScheduling: boolean
  scheduleErrorDetail: string | undefined
  scheduleErrorPending: boolean
}>()

const emit = defineEmits<{
  retry: []
  schedule: [input: { expenseAccountId: string; periods: number; startDate: LocalDate }]
  openEntry: [entryId: string]
}>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

const DEFAULT_SPREAD_PERIODS = 12

const expenseAccountId = ref('')
const spreadAcrossPeriods = ref(true)
const periods = ref(String(DEFAULT_SPREAD_PERIODS))
const startDate = ref('')

watch(
  () => props.notScheduled,
  (scheduled) => {
    if (scheduled) startDate.value = formatLocalDate(props.originEntryDate)
  },
  { immediate: true },
)

const expenseSelectOptions = computed(() =>
  props.expenseAccountOptions.map((option) => ({ value: option.id, label: option.label })),
)

function submit(): void {
  const periodCount = spreadAcrossPeriods.value ? parseInt(periods.value, 10) : 1
  if (expenseAccountId.value === '' || !Number.isInteger(periodCount) || periodCount < 1) return
  emit('schedule', {
    expenseAccountId: expenseAccountId.value,
    periods: periodCount,
    startDate: parseLocalDate(startDate.value),
  })
}

interface StatRow {
  readonly key: string
  readonly label: string
  readonly amount: Money
}

const stats = computed<readonly StatRow[]>(() => {
  const schedule = props.schedule
  if (schedule === undefined) return []
  return [
    { key: 'total', label: t('prepaidRecognitions.totalAmount'), amount: schedule.totalAmount },
    {
      key: 'recognized',
      label: t('prepaidRecognitions.recognizedAmount'),
      amount: schedule.recognizedAmount,
    },
    { key: 'pending', label: t('prepaidRecognitions.pendingAmount'), amount: schedule.pendingAmount },
    {
      key: 'cancelled',
      label: t('prepaidRecognitions.cancelledAmount'),
      amount: schedule.cancelledAmount,
    },
  ]
})

interface RecognitionRow {
  readonly id: string
  readonly date: string
  readonly amount: Money
  readonly status: PrepaidRecognitionStatus
  readonly recognitionEntryId: string | undefined
}

const rows = computed<readonly RecognitionRow[]>(() =>
  (props.schedule?.recognitions ?? []).map((recognition) => ({
    id: recognition.id,
    date: formatDate(recognition.recognitionDate, currentLocale.value),
    amount: recognition.amount,
    status: recognition.status,
    recognitionEntryId: recognition.recognitionEntryId,
  })),
)
</script>

<template>
  <section
    :aria-label="t('prepaidRecognitions.sectionLabel')"
    class="flex flex-col gap-[var(--space-inline)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-[var(--space-cell)] bg-[color:var(--color-surface)]"
  >
    <h3 class="text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]">
      {{ t('prepaidRecognitions.title') }}
    </h3>

    <p v-if="isLoading" role="status" class="text-[color:var(--color-text-muted)]">
      {{ t('prepaidRecognitions.loading') }}
    </p>

    <ErrorState
      v-else-if="error"
      :title="t('prepaidRecognitions.errorTitle')"
      :retry-label="t('prepaidRecognitions.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <template v-else-if="schedule">
      <dl class="grid grid-cols-2 gap-x-[var(--space-block)] gap-y-[var(--space-cell)] sm:grid-cols-4">
        <div v-for="stat in stats" :key="stat.key">
          <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
            {{ stat.label }}
          </dt>
          <dd class="tabular-nums text-[color:var(--color-text)]">
            <MoneyAmount :value="stat.amount" />
          </dd>
        </div>
      </dl>

      <ul class="flex flex-col divide-y divide-[color:var(--color-border)]">
        <li
          v-for="row in rows"
          :key="row.id"
          class="flex flex-wrap items-center justify-between gap-[var(--space-inline)] py-[var(--space-inline)]"
        >
          <span
            class="text-[length:var(--text-step-2)]"
            :class="
              row.status === 'CANCELLED'
                ? 'text-[color:var(--color-text-muted)] line-through'
                : 'text-[color:var(--color-text)]'
            "
          >
            {{ row.date }}
          </span>
          <span
            class="tabular-nums text-[length:var(--text-step-2)]"
            :class="row.status === 'CANCELLED' ? 'text-[color:var(--color-text-muted)] line-through' : ''"
          >
            <MoneyAmount :value="row.amount" />
          </span>
          <StatusTag v-if="row.status === 'RECOGNIZED'" tone="ok">
            {{ t('prepaidRecognitions.status.RECOGNIZED') }}
          </StatusTag>
          <span
            v-else
            class="text-[length:var(--text-step-1)] font-medium uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
            :class="row.status === 'CANCELLED' ? 'line-through' : ''"
          >
            {{ t(`prepaidRecognitions.status.${row.status}`) }}
          </span>
          <button
            v-if="row.status === 'RECOGNIZED' && row.recognitionEntryId"
            type="button"
            class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-text)]"
            @click="emit('openEntry', row.recognitionEntryId)"
          >
            {{ t('prepaidRecognitions.viewEntry') }}
          </button>
        </li>
      </ul>
    </template>

    <form v-else-if="notScheduled" class="flex flex-col gap-[var(--space-cell)]" @submit.prevent="submit">
      <p class="text-[color:var(--color-text-muted)]">{{ t('prepaidRecognitions.explanation') }}</p>

      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('prepaidRecognitions.expenseAccountLabel')
        }}</span>
        <Select
          v-model="expenseAccountId"
          :options="expenseSelectOptions"
          :placeholder="t('prepaidRecognitions.selectExpenseAccount')"
        />
      </label>

      <label class="flex items-center gap-2 text-[color:var(--color-text)]">
        <input v-model="spreadAcrossPeriods" type="checkbox" />
        {{ t('prepaidRecognitions.spreadLabel') }}
      </label>

      <div class="grid grid-cols-2 gap-[var(--space-cell)]">
        <label v-if="spreadAcrossPeriods" class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('prepaidRecognitions.periodsLabel')
          }}</span>
          <input
            v-model="periods"
            type="number"
            min="1"
            step="1"
            required
            class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('prepaidRecognitions.startDateLabel')
          }}</span>
          <input
            v-model="startDate"
            type="date"
            required
            class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
          />
        </label>
      </div>

      <p
        v-if="!spreadAcrossPeriods"
        class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
      >
        {{ t('prepaidRecognitions.singlePaymentHint') }}
      </p>

      <p class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
        {{ t('prepaidRecognitions.consequence') }}
      </p>

      <p
        v-if="scheduleErrorPending"
        class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
      >
        {{ t('prepaidRecognitions.pendingRetry') }}
      </p>
      <StatusTag v-else-if="scheduleErrorDetail" tone="danger">{{ scheduleErrorDetail }}</StatusTag>

      <ActionButton size="lg" type="submit" :disabled="isScheduling" class="self-start">
        {{ isScheduling ? t('prepaidRecognitions.scheduling') : t('prepaidRecognitions.scheduleAction') }}
      </ActionButton>
    </form>
  </section>
</template>
